import { MonitoringFinding, MonitoringResourceType } from '../../core/services/monitoring.service';
import { GlueJob, Pipeline, StateMachine } from '../../core/services/cloud.service';

/**
 * Shared model + adapters for the per-service Activity feed.
 *
 * Activity is an operational timeline, not a log console. Items are derived from
 * sources the platform already has:
 *   - monitoring findings (universal) → opened / resolved events
 *   - the most recent run/execution status already loaded on the page
 *     (Glue / CodePipeline / Step Functions) → run events
 * No new ingestion subsystem; no fabricated detail.
 */

export type ActivityCategory = 'runs' | 'failures' | 'findings' | 'config';
export type ActivitySeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
export type ActivityFilter   = 'all' | ActivityCategory;

export interface ActivityItem {
  id:         string;
  timestamp:  string;   // ISO
  resourceId: string;
  region?:    string;   // region scope of the resource ('global' for account-scoped services)
  category:   ActivityCategory;
  eventType:  string;   // short label, e.g. "Finding opened", "Run failed"
  severity:   ActivitySeverity;
  summary:    string;
  /** Whether the item drills into a resource detail (parent decides the target). */
  clickable?: boolean;
  /** Present on finding-derived items; enables inline read-only diagnosis. */
  findingId?: string;
}

const FAILURE_SEVERITIES = new Set<ActivitySeverity>(['CRITICAL', 'HIGH']);

function isFailure(item: ActivityItem): boolean {
  return item.category === 'failures' || FAILURE_SEVERITIES.has(item.severity);
}

/** Reverse-chronological. */
export function sortActivity(items: ActivityItem[]): ActivityItem[] {
  return [...items].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

export function matchesActivityFilter(item: ActivityItem, filter: ActivityFilter): boolean {
  if (filter === 'all')      return true;
  if (filter === 'failures') return isFailure(item);
  return item.category === filter;
}

/**
 * Universal adapter: expand findings for one service type into opened + resolved
 * timeline events.
 */
export function findingsToActivity(findings: MonitoringFinding[], serviceType: MonitoringResourceType): ActivityItem[] {
  const items: ActivityItem[] = [];
  for (const f of findings) {
    if (f.serviceType !== serviceType) continue;
    items.push({
      id:         `${f.findingId}:open`,
      timestamp:  f.createdAt,
      resourceId: f.resourceId,
      region:     f.region,
      category:   FAILURE_SEVERITIES.has(f.severity as ActivitySeverity) ? 'failures' : 'findings',
      eventType:  'Finding opened',
      severity:   f.severity as ActivitySeverity,
      summary:    f.title,
      clickable:  true,
      findingId:  f.findingId,
    });
    if (f.findingState === 'RESOLVED' && f.resolvedAt) {
      items.push({
        id:         `${f.findingId}:resolved`,
        timestamp:  f.resolvedAt,
        resourceId: f.resourceId,
        region:     f.region,
        category:   'findings',
        eventType:  'Finding resolved',
        severity:   'INFO',
        summary:    `Resolved: ${f.title}`,
        clickable:  true,
        findingId:  f.findingId,
      });
    }
  }
  return items;
}

// ── Service-specific run/execution adapters (derived from already-loaded lists) ──

export function glueRunsToActivity(jobs: GlueJob[]): ActivityItem[] {
  const items: ActivityItem[] = [];
  for (const j of jobs) {
    if (!j.lastRunAt || !j.lastRunStatus) continue;
    const failed = j.lastRunStatus === 'FAILED';
    items.push({
      id:         `glue-run:${j.name}:${j.lastRunAt}`,
      timestamp:  j.lastRunAt,
      resourceId: j.name,
      category:   failed ? 'failures' : 'runs',
      eventType:  `Run ${j.lastRunStatus.toLowerCase()}`,
      severity:   failed ? 'HIGH' : 'INFO',
      summary:    `${j.name} — most recent run ${j.lastRunStatus.toLowerCase()}${j.duration ? ` (${j.duration})` : ''}`,
      clickable:  true,
    });
  }
  return items;
}

export function pipelineRunsToActivity(pipelines: Pipeline[]): ActivityItem[] {
  const items: ActivityItem[] = [];
  for (const p of pipelines) {
    if (!p.lastExecutionAt) continue;
    const failed = p.status === 'Failed';
    items.push({
      id:         `pipeline-run:${p.name}:${p.lastExecutionAt}`,
      timestamp:  p.lastExecutionAt,
      resourceId: p.name,
      category:   failed || p.status === 'Stopped' ? 'failures' : 'runs',
      eventType:  `Execution ${p.status.toLowerCase()}`,
      severity:   failed ? 'HIGH' : p.status === 'Stopped' ? 'MEDIUM' : 'INFO',
      summary:    `${p.name} — most recent execution ${p.status.toLowerCase()}${p.failedStage ? ` at ${p.failedStage}` : ''}`,
      clickable:  true,
    });
  }
  return items;
}

export function stateMachineRunsToActivity(machines: StateMachine[]): ActivityItem[] {
  const items: ActivityItem[] = [];
  for (const m of machines) {
    if (!m.lastExecutionAt || !m.lastExecutionStatus) continue;
    const failed = m.lastExecutionStatus === 'FAILED' || m.lastExecutionStatus === 'TIMED_OUT' || m.lastExecutionStatus === 'ABORTED';
    items.push({
      id:         `sfn-run:${m.arn}:${m.lastExecutionAt}`,
      timestamp:  m.lastExecutionAt,
      resourceId: m.arn,
      category:   failed ? 'failures' : 'runs',
      eventType:  `Execution ${m.lastExecutionStatus.toLowerCase().replace('_', ' ')}`,
      severity:   failed ? 'HIGH' : 'INFO',
      summary:    `${m.name} — most recent execution ${m.lastExecutionStatus.toLowerCase().replace('_', ' ')}`,
      clickable:  true,
    });
  }
  return items;
}
